import { useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { uid } from '../../types';
import { uploadAvatarImage } from '../../api/dataApi';
import { queryClient } from '../../lib/queryClient';

interface AvatarImagePickerProps {
  size: number;
  onUploaded: (key: string) => void;
  onError?: (message: string) => void;
}

// A hidden <input type="file" accept="image/*"> overlaid on the avatar
// (see ProfileScreen/ChannelManageScreen/TemplateManageScreen) - tapping
// it opens the OS's own choice of camera vs. photo library on mobile
// browsers that support the `capture` attribute, or a plain file picker
// on desktop. No custom camera UI is built here (unlike CameraCapture.tsx,
// a symbol-only mock for post attachments, not a real capture path) -
// this delegates entirely to the browser/OS picker.
export function AvatarImagePicker({ size, onUploaded, onError }: AvatarImagePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // reader.result is "data:<mime>;base64,<data>" - only the part
          // after the comma is the base64 payload POST /storage expects.
          const result = reader.result as string;
          resolve(result.slice(result.indexOf(',') + 1));
        };
        reader.onerror = () => reject(reader.error ?? new Error('Unable to read file.'));
        reader.readAsDataURL(file);
      });
      const ext = file.type.split('/')[1] || 'png';
      const key = `avatar-${uid()}.${ext}`;
      // Write, not a read - retry: 0. The key is freshly generated per
      // upload, so there's no meaningful dedup case here, but routed
      // through queryClient.query anyway for the same network-layer
      // consistency as every other mutation this session.
      await queryClient.query({
        queryKey: ['avatarUpload', key],
        // Must resolve to a value: TanStack rejects a query that returns undefined
        // ("data is undefined"), even though the upload itself succeeded.
        queryFn: async () => {
          await uploadAvatarImage(key, file.type || 'application/octet-stream', base64);
          return key;
        },
        retry: 0,
      });
      onUploaded(key);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Unable to upload image.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      disabled={uploading}
      aria-label="Change avatar image"
      className="relative rounded-full bg-black/60 text-white flex items-center justify-center shrink-0 disabled:opacity-50"
      style={{ width: size * 0.34, height: size * 0.34 }}
    >
      <Camera size={size * 0.2} strokeWidth={2.2} />
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
    </button>
  );
}
